import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { type RootState } from "../../app/store";
import { type IHotelState } from "./hotelInterface";
import {
  fetchConfiguration,
  fetchHotelConfiguration,
  fetchHotelPaymentConfiguration,
  fetchHotelRoomAvailability,
} from "./hotelApi";
import { type IBookingState } from "../booking/bookingInterface";
import {
  mergePublicPricing,
  normalizeRoomCategoryAvailabilities,
} from "../../helpers/mewsPrice";

const initialState: IHotelState = {
  Id: "",
  HotelCode: "",
  EnterpriseId: "",
  ConfigurationId: "",
  DirectPaymentId: "",
  Status: "idle",
  Currencies: [],
  Languages: [],
  AcceptedCurrencyCodes: [],
  DefaultCurrencyCode: "EUR",
  DefaultLanguageCode: "en-US",
  DefaultRateCurrencyCode: "EUR",
  Name: { "en-US": "Hotel name" },
  Address: { City: "city" },
  Products: [],
  RateGroups: [],
  Rates: [],
  RoomCategories: [],
  RoomCategoryAvailabilities: [],
  CompetitorPriceRelativeAdjustment: 0,
  PaymentGateway: {
    PaymentGatewayType: "",
    IsMerchant: false,
    PublicKey: "",
    SupportedCreditCardTypes: [],
  },
  Culture: { LanguageCode: "en-US", CultureCode: "US", CurrencyCode: "EUR" },
};

// Get hotel configuration
export const getHotelConfiguration = createAsyncThunk(
  "hotel/getHotelConfiguration",
  async (Id: IHotelState["Id"], { getState }) => {
    const { hotel }: { hotel: IHotelState } = getState() as any;
    const response = await fetchHotelConfiguration(hotel, Id);
    return response.data;
  }
);

// Get hotel payment configuration
export const getHotelPaymentConfiguration = createAsyncThunk(
  "hotel/getHotelPaymentConfiguration",
  async (Id: IHotelState["Id"], { getState }) => {
    const { hotel }: { hotel: IHotelState } = getState() as any;
    const response = await fetchHotelPaymentConfiguration(hotel, Id);
    return response.data;
  }
);

// Get configuration for services
export const getConfiguration = createAsyncThunk(
  "hotel/getConfiguration",
  async (ConfigurationId: IHotelState["ConfigurationId"], { getState }) => {
    const { hotel }: { hotel: IHotelState } = getState() as any;
    const response = await fetchConfiguration(hotel, ConfigurationId);
    return response.data;
  }
);

// Get room availability
export const getRoomAvailability = createAsyncThunk(
  "hotel/getRoomAvailability",
  async (
    {
      StartUtc,
      EndUtc,
      VoucherCode,
    }: {
      StartUtc: IBookingState["StartUtc"];
      EndUtc: IBookingState["EndUtc"];
      VoucherCode: IBookingState["VoucherCode"];
    },
    { getState }
  ) => {
    const { hotel }: { hotel: IHotelState } = getState() as any;
    if (hotel.Id !== "") {
      // With a voucher Mews only returns the voucher rate for a rate group, so the public
      // prices are fetched separately to show them as the original price
      const [response, publicResponse] = await Promise.all([
        fetchHotelRoomAvailability(hotel, StartUtc, EndUtc, VoucherCode),
        VoucherCode
          ? fetchHotelRoomAvailability(hotel, StartUtc, EndUtc, null)
          : Promise.resolve(null),
      ]);
      return {
        ...response.data,
        PublicRoomCategoryAvailabilities:
          publicResponse?.data.RoomCategoryAvailabilities,
      };
    }
  }
);

export const hotelSlice = createSlice({
  name: "hotel",
  initialState,
  reducers: {
    setId: (
      state,
      action: PayloadAction<{
        Id: IHotelState["Id"];
        HotelCode: IHotelState["HotelCode"];
        ConfigurationId: IHotelState["ConfigurationId"];
        DirectPaymentId: IHotelState["DirectPaymentId"];
      }>
    ) => {
      state.Id = action.payload.Id;
      state.HotelCode = action.payload.HotelCode;
      state.ConfigurationId = action.payload.ConfigurationId;
      state.DirectPaymentId = action.payload.DirectPaymentId;
    },
    clearHotel: (state) => {
      state.Id = "";
      state.HotelCode = "";
      state.ConfigurationId = "";
      state.DirectPaymentId = "";
      state.EnterpriseId = "";
      state.Status = "idle";
      state.Currencies = [];
      state.Languages = [];
      state.AcceptedCurrencyCodes = [];
      state.DefaultCurrencyCode = "EUR";
      state.DefaultLanguageCode = "en-US";
      state.DefaultRateCurrencyCode = "EUR";
      state.Products = [];
      state.Rates = [];
      state.RoomCategories = [];
      state.RoomCategoryAvailabilities = [];
      state.CityId = undefined;
      state.ImageBaseUrl = undefined;
      state.ImageId = undefined;
      state.IntroImageId = undefined;
      state.Name = { "en-US": "Hotel name" };
      state.Address = { City: "city" };
      state.Pricing = undefined;
      state.Culture = {
        LanguageCode: "en-US",
        CultureCode: "US",
        CurrencyCode: "EUR",
      };
    },
    setCulture: (
      state,
      action: PayloadAction<{
        LanguageCode: IHotelState["Culture"]["LanguageCode"];
        CultureCode: IHotelState["Culture"]["CultureCode"];
        CurrencyCode: IHotelState["Culture"]["CurrencyCode"];
      }>
    ) => {
      state.Culture = {
        LanguageCode: action.payload.LanguageCode,
        CultureCode: action.payload.CultureCode,
        CurrencyCode: action.payload.CurrencyCode,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHotelConfiguration.pending, (state) => {
        state.Status = "loading";
      })
      .addCase(getConfiguration.fulfilled, (state, action) => {
        // console.log("<= Get config =>", action.payload);
        if (action.payload.Configurations && action.payload.Configurations[0]) {
          state.CompetitorPriceRelativeAdjustment =
            action.payload.Configurations[0].CompetitorPriceRelativeAdjustment;
          state.EnterpriseId = action.payload.Configurations[0].Enterprise.Id;
        } else {
          state.CompetitorPriceRelativeAdjustment = 1.2;
        }
      })
      .addCase(getHotelConfiguration.fulfilled, (state, action) => {
        state.Status = "loaded";
        state.CityId = action.payload.CityId;
        state.Currencies = action.payload.Currencies;
        state.Languages = action.payload.Languages;
        state.AcceptedCurrencyCodes = action.payload.AcceptedCurrencyCodes;
        state.DefaultCurrencyCode = action.payload.DefaultCurrencyCode;
        state.DefaultLanguageCode = action.payload.DefaultLanguageCode;
        state.DefaultRateCurrencyCode = action.payload.DefaultRateCurrencyCode;
        state.ImageBaseUrl = action.payload.ImageBaseUrl;
        state.ImageId = action.payload.ImageId;
        state.IntroImageId = action.payload.IntroImageId;
        state.Name = action.payload.Name;
        state.Address = action.payload.Address;
        state.Pricing = action.payload.Pricing;
        state.Products = action.payload.Products;
        state.RoomCategories = action.payload.RoomCategories;

      })
      .addCase(getHotelConfiguration.rejected, (state) => {
        state.Status = "loading";
      })
      .addCase(getHotelPaymentConfiguration.fulfilled, (state, action) => {
        // console.log("<= Get payment config =>", action.payload);
        if (
          action.payload.PaymentGateway !== null &&
          action.payload.PaymentGateway.PaymentGatewayType === "PciProxy"
        ) {
          state.PaymentGateway.PaymentGatewayType =
            action.payload.PaymentGateway.PaymentGatewayType;
          state.PaymentGateway.IsMerchant =
            action.payload.PaymentGateway.IsMerchant;
          state.PaymentGateway.PublicKey =
            action.payload.PaymentGateway.PublicKey;
          state.PaymentGateway.SupportedCreditCardTypes =
            action.payload.PaymentGateway.SupportedCreditCardTypes;
        }
      })
      .addCase(getRoomAvailability.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const hasVoucher = !!action.meta.arg.VoucherCode;
          const rates: IHotelState["Rates"] = action.payload.Rates ?? [];
          const privateRateIds = new Set(
            rates.filter((r) => r.IsPrivate).map((r) => r.Id),
          );
          const availabilities = normalizeRoomCategoryAvailabilities(
            action.payload.RoomCategoryAvailabilities,
            state.Culture.CurrencyCode,
          );

          state.RateGroups = action.payload.RateGroups;
          state.Rates = hasVoucher ? rates : rates.filter((r) => !r.IsPrivate);
          state.RoomCategoryAvailabilities = hasVoucher
            ? mergePublicPricing(
                availabilities,
                normalizeRoomCategoryAvailabilities(
                  action.payload.PublicRoomCategoryAvailabilities,
                  state.Culture.CurrencyCode,
                ),
              )
            : availabilities.map((rca) => ({
                ...rca,
                RoomOccupancyAvailabilities: rca.RoomOccupancyAvailabilities.map(
                  (roa) => ({
                    ...roa,
                    Pricing: roa.Pricing.filter(
                      (p) => !privateRateIds.has(p.RateId),
                    ),
                  }),
                ),
              }));
        }
      })
      .addCase(getRoomAvailability.rejected, (state, action) => {
        console.log("getRoomAvailability.rejected", action.payload);
        // Keep hotel identity; wiping HotelCode made VAT lookups crash on .rooms
        state.RoomCategoryAvailabilities = [];
      });
  },
});

export const { setId, clearHotel, setCulture } = hotelSlice.actions;

// Selectors
export const selectId = (state: RootState) => state.hotel.Id;
export const selectHotel = (state: RootState) => state.hotel;

export default hotelSlice.reducer;
